import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Table, Button, Tag, Space, message, Modal, Select, DatePicker, Typography, Input, Tabs,
  Popconfirm, Segmented, Spin, Empty, Row, Col, Statistic,
} from 'antd';
import {
  CheckCircleOutlined, UserAddOutlined, ReloadOutlined, HistoryOutlined, TeamOutlined,
  ClockCircleOutlined, EnvironmentOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const MOBILE_BREAKPOINT = 768;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const respuestaBotColor = {
  SI: 'green',
  NO: 'red',
  SIN_RESPUESTA: 'default',
};

const cardMobileStyle = (r) => ({
  borderRadius: 12,
  borderLeft: `4px solid ${r.ya_fichado ? '#52c41a' : '#faad14'}`,
});

const FichadaAdmin = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [fecha, setFecha] = useState(dayjs());
  const [jugadores, setJugadores] = useState([]);
  const [fichadasProfesores, setFichadasProfesores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProfesores, setLoadingProfesores] = useState(false);
  const [fichando, setFichando] = useState(null);
  const [modalAgregar, setModalAgregar] = useState(false);
  const [alumnos, setAlumnos] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [alumnoAgregar, setAlumnoAgregar] = useState(null);
  const [turnoAgregar, setTurnoAgregar] = useState(null);
  const [agregando, setAgregando] = useState(false);
  const [nombreFiltro, setNombreFiltro] = useState('');
  const [nombreFiltroProf, setNombreFiltroProf] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [tabActiva, setTabActiva] = useState('jugadores');

  const fechaStr = fecha.format('YYYY-MM-DD');

  const jugadoresPorNombre = useMemo(() => jugadores.filter((j) => {
    if (!nombreFiltro.trim()) return true;
    const termino = nombreFiltro.trim().toLowerCase();
    const nombreCompleto = `${j.alumno_nombre || ''} ${j.alumno_apellido || ''}`.toLowerCase();
    return nombreCompleto.includes(termino);
  }), [jugadores, nombreFiltro]);

  const jugadoresFiltrados = useMemo(() => jugadoresPorNombre.filter((j) => {
    if (filtroEstado === 'fichados') return j.ya_fichado;
    if (filtroEstado === 'pendientes') return !j.ya_fichado;
    return true;
  }), [jugadoresPorNombre, filtroEstado]);

  const statsJugadores = useMemo(() => ({
    total: jugadoresPorNombre.length,
    fichados: jugadoresPorNombre.filter((j) => j.ya_fichado).length,
    pendientes: jugadoresPorNombre.filter((j) => !j.ya_fichado).length,
  }), [jugadoresPorNombre]);

  const profesoresFiltrados = fichadasProfesores.filter((f) => {
    if (!nombreFiltroProf.trim()) return true;
    const termino = nombreFiltroProf.trim().toLowerCase();
    const nombreCompleto = `${f.profesor_nombre || ''} ${f.profesor_apellido || ''}`.toLowerCase();
    return nombreCompleto.includes(termino);
  });

  const cargarJugadores = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/fichada/del-dia?fecha=${fechaStr}`);
      if (res.ok) {
        setJugadores(await res.json());
      } else {
        message.error('Error al cargar jugadores del día');
      }
    } catch {
      message.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const cargarProfesores = async () => {
    setLoadingProfesores(true);
    try {
      const res = await apiClient.get(`/fichada/profesores-del-dia?fecha=${fechaStr}`);
      if (res.ok) {
        setFichadasProfesores(await res.json());
      } else {
        message.error('Error al cargar fichadas de profesores');
      }
    } catch {
      message.error('Error de conexión');
    } finally {
      setLoadingProfesores(false);
    }
  };

  const cargar = () => {
    cargarJugadores();
    cargarProfesores();
  };

  useEffect(() => {
    cargar();
  }, [fechaStr]);

  const ficharJugador = async (record) => {
    const loadingKey = `${record.alumno_id}-${record.turno_id}`;
    setFichando(loadingKey);
    try {
      const res = await apiClient.post('/fichada/registrar', {
        alumnoId: record.alumno_id,
        turnoId: record.turno_id,
      });
      const data = await res.json();
      if (res.ok) {
        message.success(`Fichada: ${record.alumno_nombre} ${record.alumno_apellido}`);
        cargarJugadores();
      } else {
        message.error(data.message);
      }
    } catch {
      message.error('Error al fichar');
    } finally {
      setFichando(null);
    }
  };

  const abrirAgregar = async () => {
    setModalAgregar(true);
    try {
      const [resAlumnos, resTurnos] = await Promise.all([
        apiClient.get('/alumnos?pageSize=500'),
        apiClient.get(`/turnos/rango?fechaDesde=${fechaStr}&fechaHasta=${fechaStr}&fechaConsulta=${fechaStr}`),
      ]);
      if (resAlumnos.ok) {
        const d = await resAlumnos.json();
        setAlumnos(d.data || d || []);
      }
      if (resTurnos.ok) {
        setTurnos(await resTurnos.json());
      }
    } catch {
      message.error('Error al cargar datos');
    }
  };

  const confirmarAgregar = async () => {
    if (!alumnoAgregar || !turnoAgregar) {
      message.warning('Seleccione jugador y turno');
      return;
    }
    setAgregando(true);
    try {
      const res = await apiClient.post('/fichada/agregar-alumno', {
        alumnoId: alumnoAgregar,
        turnoId: turnoAgregar,
        fecha: fechaStr,
      });
      if (res.ok) {
        message.success('Jugador agregado al turno');
        setModalAgregar(false);
        setAlumnoAgregar(null);
        setTurnoAgregar(null);
        cargarJugadores();
      } else {
        const d = await res.json();
        message.error(d.message);
      }
    } catch {
      message.error('Error al agregar');
    } finally {
      setAgregando(false);
    }
  };

  const renderBotonFichar = (r, block = false) => {
    if (r.ya_fichado) return null;
    const loadingKey = `${r.alumno_id}-${r.turno_id}`;
    const boton = (
      <Button
        type="primary"
        size={block ? 'large' : 'small'}
        block={block}
        danger={r.asiste === false}
        loading={fichando === loadingKey}
        onClick={r.asiste === false ? undefined : () => ficharJugador(r)}
        style={block ? { height: 48, fontWeight: 600, marginTop: 8 } : undefined}
      >
        {r.asiste === false ? 'Fichar igual' : 'Fichar'}
      </Button>
    );

    if (r.asiste === false) {
      return (
        <Popconfirm
          title="Marcó que no asiste"
          description="¿Registrar la fichada de todas formas?"
          onConfirm={() => ficharJugador(r)}
          okText="Sí, fichar"
          cancelText="Cancelar"
        >
          {boton}
        </Popconfirm>
      );
    }
    return boton;
  };

  const renderAsisteTag = (v) => {
    if (v === true) return <Tag color="green">Asiste</Tag>;
    if (v === false) return <Tag color="red">No asiste</Tag>;
    return <Tag>Sin definir</Tag>;
  };

  const renderJugadorCard = (r) => (
    <Card key={`${r.alumno_id}-${r.turno_id}`} size="small" style={cardMobileStyle(r)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 16, display: 'block' }}>
            {r.alumno_nombre} {r.alumno_apellido}
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>{r.alumno_telefono || 'Sin teléfono'}</Text>
        </div>
        {r.ya_fichado
          ? <Tag color="green" icon={<CheckCircleOutlined />} style={{ margin: 0 }}>Fichado</Tag>
          : <Tag color="orange" style={{ margin: 0 }}>Pendiente</Tag>}
      </div>

      <div style={{
        background: 'rgba(24, 144, 255, 0.08)',
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 10,
      }}>
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <Text strong style={{ fontSize: 15 }}>
            {r.hora_desde?.substring(0, 5)} – {r.hora_hasta?.substring(0, 5)}
          </Text>
        </Space>
        {r.cancha && (
          <div style={{ marginTop: 4, fontSize: 13 }}>
            <EnvironmentOutlined style={{ marginRight: 6, color: '#888' }} />
            {r.cancha}
            {r.categoria ? ` · ${r.categoria}` : ''}
          </div>
        )}
      </div>

      <Space wrap size={[4, 8]} style={{ marginBottom: 8 }}>
        {renderAsisteTag(r.asiste)}
        <Tag color={respuestaBotColor[r.respuesta_bot] || 'default'}>
          Bot: {r.respuesta_bot === 'SIN_RESPUESTA' ? 'Sin resp.' : r.respuesta_bot}
        </Tag>
        <Tag color={r.saldo_creditos <= 0 ? 'orange' : 'blue'}>
          {r.saldo_creditos} créd.
        </Tag>
      </Space>

      {renderBotonFichar(r, true)}
    </Card>
  );

  const renderProfesorCard = (r) => (
    <Card key={r.fip_id} size="small" style={{ borderRadius: 12, borderLeft: '4px solid #722ed1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text strong style={{ fontSize: 20, color: '#1890ff', display: 'block' }}>
            {dayjs(r.fip_hora).format('HH:mm')}
          </Text>
          <Text strong style={{ fontSize: 16 }}>
            {r.profesor_nombre} {r.profesor_apellido}
          </Text>
          <div><Text type="secondary" style={{ fontSize: 13 }}>@{r.profesor_usuario || '-'}</Text></div>
        </div>
        <Tag color={{ PROFESOR: 'blue', ADMIN: 'purple', FICHADA: 'cyan' }[r.fip_tipo] || 'default'}>
          {r.fip_tipo}
        </Tag>
      </div>
    </Card>
  );

  const listaJugadoresMobile = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : jugadoresFiltrados.length === 0 ? (
        <Empty description="No hay jugadores para mostrar" />
      ) : (
        jugadoresFiltrados.map(renderJugadorCard)
      )}
    </div>
  );

  const listaProfesoresMobile = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {loadingProfesores ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : profesoresFiltrados.length === 0 ? (
        <Empty description="Sin ingresos de profesores hoy" />
      ) : (
        profesoresFiltrados.map(renderProfesorCard)
      )}
    </div>
  );

  const columnsJugadores = [
    {
      title: 'Jugador',
      key: 'jugador',
      fixed: isMobile ? undefined : 'left',
      width: 180,
      render: (_, r) => (
        <div>
          <Text strong>{r.alumno_nombre} {r.alumno_apellido}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.alumno_telefono || '-'}</Text></div>
        </div>
      ),
    },
    {
      title: 'Horario',
      key: 'horario',
      width: 110,
      render: (_, r) => `${r.hora_desde?.substring(0, 5)} - ${r.hora_hasta?.substring(0, 5)}`,
    },
    { title: 'Cancha', dataIndex: 'cancha', key: 'cancha', width: 100, responsive: ['md'] },
    { title: 'Categoría', dataIndex: 'categoria', key: 'categoria', width: 100, responsive: ['lg'] },
    {
      title: 'Asiste',
      dataIndex: 'asiste',
      key: 'asiste',
      width: 90,
      render: (v) => renderAsisteTag(v),
    },
    {
      title: 'Bot',
      dataIndex: 'respuesta_bot',
      key: 'respuesta_bot',
      width: 100,
      responsive: ['md'],
      render: (v) => (
        <Tag color={respuestaBotColor[v] || 'default'}>
          {v === 'SIN_RESPUESTA' ? 'Sin resp.' : v}
        </Tag>
      ),
    },
    {
      title: 'Créd.',
      dataIndex: 'saldo_creditos',
      key: 'saldo',
      width: 70,
      render: (v) => <Tag color={v <= 0 ? 'orange' : 'blue'}>{v}</Tag>,
    },
    {
      title: 'Estado',
      key: 'fichada',
      width: 100,
      render: (_, r) => r.ya_fichado
        ? <Tag color="green" icon={<CheckCircleOutlined />}>Fichado</Tag>
        : <Tag color="default">Pendiente</Tag>,
    },
    {
      title: '',
      key: 'accion',
      width: 110,
      fixed: isMobile ? undefined : 'right',
      render: (_, r) => renderBotonFichar(r, false),
    },
  ];

  const columnsProfesores = [
    {
      title: 'Hora',
      dataIndex: 'fip_hora',
      key: 'hora',
      width: 80,
      render: (v) => <Text strong>{dayjs(v).format('HH:mm')}</Text>,
    },
    {
      title: 'Profesor',
      key: 'profesor',
      render: (_, r) => (
        <div>
          <Text strong>{r.profesor_nombre} {r.profesor_apellido}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{r.profesor_usuario || '-'}</Text></div>
        </div>
      ),
    },
    { title: 'DNI', dataIndex: 'profesor_documento', key: 'dni', responsive: ['md'], render: (v) => v || '-' },
    { title: 'Tel.', dataIndex: 'profesor_telefono', key: 'tel', responsive: ['lg'], render: (v) => v || '-' },
    {
      title: 'Por',
      dataIndex: 'fip_tipo',
      key: 'tipo',
      width: 90,
      render: (v) => {
        const colors = { PROFESOR: 'blue', ADMIN: 'purple', FICHADA: 'cyan' };
        return <Tag color={colors[v] || 'default'}>{v}</Tag>;
      },
    },
  ];

  const resumenJugadores = (
    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
      <Col xs={8}>
        <Card size="small" styles={{ body: { padding: isMobile ? '12px 8px' : undefined } }}>
          <Statistic title="Total" value={statsJugadores.total} valueStyle={{ fontSize: isMobile ? 20 : 24 }} />
        </Card>
      </Col>
      <Col xs={8}>
        <Card size="small" styles={{ body: { padding: isMobile ? '12px 8px' : undefined } }}>
          <Statistic
            title="Fichados"
            value={statsJugadores.fichados}
            valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 24 }}
          />
        </Card>
      </Col>
      <Col xs={8}>
        <Card size="small" styles={{ body: { padding: isMobile ? '12px 8px' : undefined } }}>
          <Statistic
            title="Pendientes"
            value={statsJugadores.pendientes}
            valueStyle={{ color: '#faad14', fontSize: isMobile ? 20 : 24 }}
          />
        </Card>
      </Col>
    </Row>
  );

  const toolbarJugadores = (
    <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
      <Input.Search
        placeholder="Buscar jugador..."
        allowClear
        value={nombreFiltro}
        onChange={(e) => setNombreFiltro(e.target.value)}
        size={isMobile ? 'large' : 'middle'}
        style={{ width: '100%' }}
      />
      <Segmented
        block
        size="large"
        value={filtroEstado}
        onChange={setFiltroEstado}
        options={[
          { label: `Todos (${statsJugadores.total})`, value: 'todos' },
          { label: `Pend. (${statsJugadores.pendientes})`, value: 'pendientes' },
          { label: `Ok (${statsJugadores.fichados})`, value: 'fichados' },
        ]}
      />
      <Button
        type="primary"
        icon={<UserAddOutlined />}
        onClick={abrirAgregar}
        block={isMobile}
        size={isMobile ? 'large' : 'middle'}
      >
        Agregar jugador
      </Button>
    </Space>
  );

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '0 0 16px' : undefined }}>
        <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, justifyContent: 'space-between' }}>
              <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>Fichada del día</Title>
              <Space wrap style={{ width: isMobile ? '100%' : 'auto' }}>
                <DatePicker
                  value={fecha}
                  onChange={(d) => d && setFecha(d)}
                  format="DD/MM/YYYY"
                  size={isMobile ? 'large' : 'middle'}
                  style={{ width: isMobile ? '100%' : undefined }}
                  inputReadOnly
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={cargar}
                  size={isMobile ? 'large' : 'middle'}
                  block={isMobile}
                >
                  Actualizar
                </Button>
                <Button
                  icon={<HistoryOutlined />}
                  onClick={() => navigate('/historial-fichadas')}
                  size={isMobile ? 'large' : 'middle'}
                  block={isMobile}
                >
                  Historial
                </Button>
              </Space>
            </div>

            <Tabs
              activeKey={tabActiva}
              onChange={setTabActiva}
              size={isMobile ? 'large' : 'middle'}
              centered={isMobile}
              items={[
                {
                  key: 'jugadores',
                  label: `Jugadores (${statsJugadores.total})`,
                  children: (
                    <>
                      {resumenJugadores}
                      {toolbarJugadores}
                      {isMobile ? listaJugadoresMobile : (
                        <Table
                          columns={columnsJugadores}
                          dataSource={jugadoresFiltrados}
                          rowKey={(r) => `${r.alumno_id}-${r.turno_id}`}
                          loading={loading}
                          size="middle"
                          pagination={{
                            pageSize: 20,
                            showTotal: (total) => `${total} jugadores`,
                            simple: isMobile,
                          }}
                          scroll={{ x: 960 }}
                        />
                      )}
                    </>
                  ),
                },
                {
                  key: 'profesores',
                  label: (
                    <span><TeamOutlined /> Profesores ({profesoresFiltrados.length})</span>
                  ),
                  children: (
                    <>
                      <Input.Search
                        placeholder="Buscar profesor..."
                        allowClear
                        value={nombreFiltroProf}
                        onChange={(e) => setNombreFiltroProf(e.target.value)}
                        size={isMobile ? 'large' : 'middle'}
                        style={{ width: '100%', marginBottom: 16 }}
                      />
                      {isMobile ? listaProfesoresMobile : (
                        <Table
                          columns={columnsProfesores}
                          dataSource={profesoresFiltrados}
                          rowKey="fip_id"
                          loading={loadingProfesores}
                          pagination={{
                            pageSize: 20,
                            showTotal: (total) => `${total} ingresos`,
                            simple: isMobile,
                          }}
                          scroll={{ x: 600 }}
                        />
                      )}
                    </>
                  ),
                },
              ]}
            />
          </Space>
        </Card>
      </div>

      <Modal
        title="Agregar jugador a un turno"
        open={modalAgregar}
        onCancel={() => setModalAgregar(false)}
        onOk={confirmarAgregar}
        confirmLoading={agregando}
        okText="Agregar"
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 20, maxWidth: '100vw', padding: 0 } : undefined}
        styles={isMobile ? { body: { padding: '16px 12px' } } : undefined}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text>Jugador</Text>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Buscar jugador"
              optionFilterProp="label"
              value={alumnoAgregar}
              onChange={setAlumnoAgregar}
              size="large"
              options={alumnos.filter((a) => a.alu_activo !== false).map((a) => ({
                value: a.alu_id,
                label: `${a.alu_nombre} ${a.alu_apellido}`,
              }))}
            />
          </div>
          <div>
            <Text>Turno del día</Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Seleccionar turno"
              value={turnoAgregar}
              onChange={setTurnoAgregar}
              size="large"
              options={turnos.map((t) => ({
                value: t.tur_id,
                label: `${t.tur_hora_desde?.substring(0, 5)} - ${t.cancha_descripcion || 'Cancha'} (${t.categoria_descripcion || ''})`,
              }))}
            />
          </div>
        </Space>
      </Modal>
    </MainLayout>
  );
};

export default FichadaAdmin;
